import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import ContractDocument from '../components/ContractDocument';
import CameraCapture from '../components/CameraCapture';
import { buildContractContext } from '../utils/contractContext';
import { renderContract, printContract } from '../utils/renderContract';
import { DAMAGE_TYPES, DAMAGE_SEVERITIES } from '../data/damages';

// AddLeaseModal — operator-only long-term lease creation, 4-step wizard.
// Saves the lease as a rental with type='lease' so it lives alongside
// short-term rentals in the same data model, calendar, and customer history.

const LEASE_STEPS = ['Vehicle & Customer', 'Lease Terms', 'Photos & Condition', 'Agreement & Sign'];
const LEASE_COND_OPTIONS = [
  { value: 'no_damage',     label: '✅ No damage — clean' },
  { value: 'small_scratch', label: '🔸 Small scratch / scuff' },
  { value: 'damage',        label: '⚠️ Visible damage' },
  { value: 'major_damage',  label: '🔴 Major damage / accident' },
];
const LEASE_PHOTO_SLOTS = ['Front', 'Rear', 'Left side', 'Right side', 'Dashboard', 'Interior'];

// ── Combobox (searchable picker) ───────────────────────────────────────────
// Reusable inline combobox for vehicle and customer pickers.
// Props:
//   value (selected item id)
//   items [{ id, label, sublabel?, icon? }]
//   placeholder
//   onSelect(id)
//   onClear()
//   extraOption (e.g. { id: '__new__', label: '+ New customer' })
function Combobox({ value, items, placeholder, onSelect, onClear, extraOption }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);   // { left, top, width, openUp } — fixed-positioned dropdown coords
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const selected = items.find(it => it.id === value);

  // Recompute dropdown coords from input rect — always anchored below.
  // If not enough room, scroll the input into view first so the dropdown doesn't cover other fields.
  function recalc() {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxH = 260;
    setPos({ left: r.left, width: r.width, top: r.bottom + 4, maxH });
  }
  // On first open, if there isn't room below, scroll the input towards the top of the viewport
  // (centered-ish) so the dropdown opens cleanly underneath it.
  function ensureRoomBelow() {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxH = 260;
    const spaceBelow = window.innerHeight - r.bottom;
    if (spaceBelow < maxH) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  // Close on outside click (covers both the input wrapper AND the portal-rendered dropdown)
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      const insideWrap = wrapRef.current?.contains(e.target);
      const insideDrop = dropRef.current?.contains(e.target);
      if (!insideWrap && !insideDrop) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Recompute coords on open + on scroll/resize while open
  useEffect(() => {
    if (!open) { setPos(null); return; }
    ensureRoomBelow();
    // Small delay so the scrollIntoView happens before we measure
    const t = setTimeout(recalc, 50);
    function onScrollResize() { recalc(); }
    window.addEventListener('scroll', onScrollResize, true);  // capture = catch nested scrolls (modal body)
    window.addEventListener('resize', onScrollResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter(it => {
        const hay = (it.label + ' ' + (it.sublabel || '')).toLowerCase();
        return hay.includes(q);
      })
    : items;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {selected ? (
        <div className="fi" style={{
          display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between',
          background: 'var(--ac-l)', borderColor: 'var(--ac)', cursor: 'default',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            {selected.icon && <span style={{ fontSize: 16 }}>{selected.icon}</span>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selected.label}
              </div>
              {selected.sublabel && (
                <div style={{ fontSize: 10, color: 'var(--tx3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selected.sublabel}
                </div>
              )}
            </div>
          </div>
          <button type="button" onClick={onClear}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 16, padding: 4 }}>
            ×
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="text"
            className="fi"
            placeholder={placeholder}
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
          />
          {open && pos && createPortal(
            <div ref={dropRef} style={{
              position: 'fixed', left: pos.left, top: pos.top, width: pos.width,
              background: 'var(--sur)', border: '1px solid var(--bd2)', borderRadius: 'var(--r)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 10001,
              maxHeight: pos.maxH, overflowY: 'auto',
            }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '10px 12px', fontSize: 11, color: 'var(--tx3)', fontStyle: 'italic' }}>
                  No matches
                </div>
              ) : (
                filtered.map(it => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => { onSelect(it.id); setSearch(''); setOpen(false); }}
                    style={{
                      width: '100%', textAlign: 'left', background: 'none', border: 'none',
                      padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                      borderBottom: '1px solid var(--bd)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--ac-l)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {it.icon && <span style={{ fontSize: 16 }}>{it.icon}</span>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it.label}
                      </div>
                      {it.sublabel && (
                        <div style={{ fontSize: 10, color: 'var(--tx3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {it.sublabel}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
              {extraOption && (
                <button
                  type="button"
                  onClick={() => { onSelect(extraOption.id); setSearch(''); setOpen(false); }}
                  style={{
                    width: '100%', textAlign: 'left', background: 'var(--sur2)', border: 'none',
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    color: 'var(--ac)', borderTop: '1px solid var(--bd)',
                  }}
                >
                  {extraOption.label}
                </button>
              )}
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
}

const TERM_OPTIONS = [
  { months: 6,  label: '6 months' },
  { months: 12, label: '12 months' },
  { months: 24, label: '24 months' },
  { months: 36, label: '36 months' },
  { months: 48, label: '48 months' },
];

const MILEAGE_OPTIONS = [
  { value: 0,      label: 'Unlimited' },
  { value: 15000,  label: '15,000 km / year' },
  { value: 20000,  label: '20,000 km / year' },
  { value: 25000,  label: '25,000 km / year' },
  { value: 30000,  label: '30,000 km / year' },
  { value: 40000,  label: '40,000 km / year' },
];

const BLANK_FORM = {
  // Customer
  customerType: 'company',        // 'company' (B2B) or 'individual' (B2C)
  customerId: '',                  // existing customer id or '__new__'
  customerName: '',
  customerCompany: '',
  vatNumber: '',
  phone: '',
  email: '',
  // Lease
  vid: '',
  start: '',
  termMonths: 12,
  monthlyRate: '',
  deposit: '',
  annualMileageCap: 20000,
  includedServices: 'Maintenance and insurance included.',
  notes: '',
  payMethod: 'bank',
  depositMethod: 'Bank guarantee',
  billingCadence: 'advance',       // 'advance' (pay before month) | 'end_of_month' | 'net_15' | 'net_30'
  vatMode: 'inclusive',            // 'inclusive' (price includes VAT) | 'exclusive' (+ VAT on top)
  vatRate: '',                     // optional override of company VAT rate (empty = use company default)
  pickupLocId: '',
  agreementId: '',                 // selected agreement template id
  signed: false,
  signatureDataUrl: '',            // base64 PNG of customer signature
  signedAt: '',                    // ISO timestamp
};

const BILLING_CADENCES = [
  { id: 'advance',      label: 'Advance — bill before each month starts (most common)' },
  { id: 'end_of_month', label: 'End of month — bill on the last day' },
  { id: 'net_15',       label: 'NET-15 — pro forma issued, payable in 15 days' },
  { id: 'net_30',       label: 'NET-30 — pro forma issued, payable in 30 days' },
];

function addMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const target = new Date(d.getFullYear(), d.getMonth() + months, d.getDate());
  return target.toISOString().slice(0, 10);
}

export default function AddLeaseModal() {
  const {
    modals, closeModal,
    vehicles, customers, locations, rentals,
    nextContractId, showToast, companySettings,
    addCustomer, addRental, addInvoice, agreementTemplates, resolveAgreementTemplate,
    nextInvoiceNum,
    getDamagesForVehicle,
  } = useApp();

  const open = modals['add-lease'];
  // Preset can be passed via openModal('add-lease', { preset, isRenewal })
  const payload = modals['add-lease-id'] || modals['add-lease-vid'];
  const preset = (payload && typeof payload === 'object') ? payload : null;
  const isRenewal = !!preset?.isRenewal;

  const [form, setForm] = useState(BLANK_FORM);
  const [showSignature, setShowSignature] = useState(false);
  const [showPreview, setShowPreview] = useState(false);   // contract preview overlay
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  // 4-step wizard state
  const [step, setStep] = useState(1);
  // Vehicle condition + handover photos (new Step 3)
  // photos = object keyed by slot label so each slot can hold one image, mirrors CheckInModal
  const [photos, setPhotos] = useState({});   // { 'Front': dataUrl, 'Rear': dataUrl, ... }
  const [cond, setCond] = useState('no_damage');
  const [condNote, setCondNote] = useState('');
  const [camera, setCamera] = useState(null);        // when set, CameraCapture overlay renders
  const [activeSlot, setActiveSlot] = useState(null);
  const slotFileRef = useRef(null);

  // Slot helpers (mirror CheckInModal pattern)
  function openSlotFile(label) { setActiveSlot(label); slotFileRef.current?.click(); }
  function onSlotFile(e) {
    const file = e.target.files?.[0];
    if (!file || !activeSlot) return;
    const reader = new FileReader();
    reader.onload = ev => setPhotos(prev => ({ ...prev, [activeSlot]: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function openSlotCamera(label) {
    setCamera({
      label: `${label} — Handover`,
      setPhoto: (data) => setPhotos(prev => ({ ...prev, [label]: data })),
    });
  }
  function removeSlotPhoto(label) {
    setPhotos(prev => { const n = { ...prev }; delete n[label]; return n; });
  }

  // ── Shared helper: resolve template + build context for the current form state ──
  // Used by both the Preview button (renders <ContractDocument>) and the Print button (printContract).
  function getCurrentTmplCtx() {
    const v = vehicles.find(vv => vv.id === form.vid);
    const endDt = addMonths(form.start, form.termMonths);
    const total = (parseFloat(form.monthlyRate) || 0) * (parseInt(form.termMonths) || 0);
    const tmpl = (agreementTemplates || []).find(t => t.id === form.agreementId)
      || (resolveAgreementTemplate ? resolveAgreementTemplate(form.vid, 'lease') : null)
      || (agreementTemplates || []).find(t => t.appliesTo === 'lease')
      || (agreementTemplates || []).find(t => t.isDefault)
      || (agreementTemplates || [])[0];
    const customerDisplay = form.customerType === 'company'
      ? (form.customerCompany || form.customerName)
      : form.customerName;
    const ctx = buildContractContext({
      rental: {
        customer: customerDisplay,
        customerCompany: form.customerCompany,
        passport: form.passport, licence: form.licence,
        email: form.email, phone: form.phone, vatNumber: form.vatNumber,
        startLabel: form.start ? new Date(form.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
        endLabel:   endDt ? new Date(endDt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
        termMonths: form.termMonths,
        monthlyRate: form.monthlyRate,
        annualMileageCap: parseInt(form.annualMileageCap) || 0,
        deposit: form.deposit, depositMethod: form.depositMethod,
        agreedPrice: total,
        includedServices: form.includedServices,
        pickupLocName: locations.find(l => l.id === form.pickupLocId)?.name,
      },
      vehicle: v,
      company: companySettings,
      signatureDataUrl: form.signatureDataUrl,
      signedAt: form.signedAt,
    });
    return { tmpl, ctx };
  }

  // Reset when opened — apply preset if present (e.g. when renewing a previous lease)
  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      // Reset wizard state every time the modal opens
      setStep(1);
      setPhotos({});
      setCond('no_damage');
      setCondNote('');
      setCamera(null);
      setActiveSlot(null);
      setShowPreview(false);
      if (preset) {
        // Carry forward everything from the old lease, but reset start date,
        // signature, and invoiced months tracking (it's a NEW contract).
        setForm({
          ...BLANK_FORM,
          customerType:     preset.customerType     || (preset.customerCompany ? 'company' : 'individual'),
          customerId:       preset.customerId       || '',
          customerName:     preset.customerName     || preset.customer || '',
          customerCompany:  preset.customerCompany  || '',
          vatNumber:        preset.vatNumber        || '',
          phone:            preset.phone            || '',
          email:            preset.email            || '',
          vid:              preset.vid              || '',
          start:            preset.start            || today,
          termMonths:       preset.termMonths       || 12,
          monthlyRate:      preset.monthlyRate      || '',
          deposit:          preset.deposit          || '',
          annualMileageCap: preset.annualMileageCap ?? 20000,
          includedServices: preset.includedServices || 'Maintenance and insurance included.',
          notes:            preset.notes            || '',
          payMethod:        preset.payMethod        || 'bank',
          depositMethod:    preset.depositMethod    || 'Bank guarantee',
          billingCadence:   preset.billingCadence   || 'advance',
          vatMode:          preset.vatMode          || companySettings?.vatMode || 'inclusive',
          vatRate:          preset.vatRate          || '',
          pickupLocId:      preset.pickupLocId      || locations?.[0]?.id || '',
          agreementId:      preset.agreementId      || '',
        });
      } else {
        setForm({
          ...BLANK_FORM,
          start: today,
          pickupLocId: locations?.[0]?.id || '',
          vatMode: companySettings?.vatMode || 'inclusive',
          vatRate: '', // empty = use company default
        });
      }
    }
  }, [open]); // eslint-disable-line

  // Filter to available vehicles only (not in maintenance, not in another active lease)
  // NOTE: useMemo must be called before any early return to satisfy React rules of hooks.
  const availableVehicles = useMemo(() => {
    return vehicles.filter(v => {
      // Block if vehicle is in an active lease already
      const activeLease = rentals.find(r => r.vid === v.id && r.type === 'lease' && r.status === 'active');
      if (activeLease) return false;
      return true;
    });
  }, [vehicles, rentals]);

  if (!open) return null;

  function F(k) { return { value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) }; }

  // Derived
  const endDate = addMonths(form.start, form.termMonths);
  const totalContract = (parseFloat(form.monthlyRate) || 0) * (form.termMonths || 0);
  const selectedVehicle = vehicles.find(v => v.id === form.vid);
  const isNewCust = form.customerId === '__new__';

  function pickCustomer(id) {
    if (id === '__new__') {
      setForm(p => ({ ...p, customerId: '__new__', customerName: '', customerCompany: '', vatNumber: '', phone: '', email: '' }));
      return;
    }
    if (!id) {
      setForm(p => ({ ...p, customerId: '', customerName: '', customerCompany: '', vatNumber: '', phone: '', email: '' }));
      return;
    }
    const c = customers.find(cc => cc.id === id);
    if (!c) return;
    setForm(p => ({
      ...p,
      customerId: id,
      customerName: `${c.fname} ${c.lname}`.trim() || c.company || '',
      customerCompany: c.company || '',
      vatNumber: c.vatNumber || '',
      phone: c.phone || '',
      email: c.email || '',
    }));
  }

  function submit() {
    // Validation
    if (!form.vid)         { showToast('Please select a vehicle'); return; }
    if (!form.start)       { showToast('Start date is required'); return; }
    if (!form.monthlyRate) { showToast('Monthly rate is required'); return; }
    if (!form.customerName && !form.customerCompany) {
      showToast(form.customerType === 'company' ? 'Company name required' : 'Customer name required');
      return;
    }

    const customerDisplay = form.customerType === 'company'
      ? (form.customerCompany || form.customerName)
      : form.customerName;

    // Create new customer record if needed
    let custId = form.customerId;
    if (isNewCust || !custId) {
      const parts = (form.customerName || form.customerCompany).split(' ');
      const newCust = {
        id: 'C-' + Date.now().toString(36).slice(-6),
        fname: parts[0] || (form.customerCompany || 'Customer'),
        lname: parts.slice(1).join(' ') || '',
        company: form.customerCompany || '',
        vatNumber: form.vatNumber || '',
        phone: form.phone || '',
        email: form.email || '',
        address: '',
      };
      addCustomer(newCust);
      custId = newCust.id;
    }

    // Build the lease rental
    const id = nextContractId();
    const pickLoc = locations.find(l => l.id === form.pickupLocId);
    const startDt = form.start + 'T09:00';
    const endDt   = endDate + 'T09:00';
    const lease = {
      id,
      type: 'lease',
      vid: form.vid,
      status: 'active',
      customer: customerDisplay,
      customerCompany: form.customerCompany || null,
      vatNumber: form.vatNumber || null,
      customerId: custId,
      phone: form.phone || '',
      email: form.email || '',
      start: startDt,
      end: endDt,
      t1: '09:00', t2: '09:00',
      startLabel: new Date(form.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      endLabel:   new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      pickupLocId: form.pickupLocId, pickupLocName: pickLoc?.name || '',
      returnLocId: form.pickupLocId, returnLocName: pickLoc?.name || '',
      termMonths: form.termMonths,
      monthlyRate: parseFloat(form.monthlyRate) || 0,
      annualMileageCap: parseInt(form.annualMileageCap) || 0,
      includedServices: form.includedServices,
      invoicedMonths: [],
      deposit: parseFloat(form.deposit) || 0,
      depositMethod: form.depositMethod,
      payMethod: form.payMethod,
      billingCadence: form.billingCadence,
      vatMode: form.vatMode || 'inclusive',
      vatRate: parseFloat(form.vatRate) || parseFloat(companySettings?.vatRate || 19),
      agreedPrice: totalContract,
      notes: form.notes,
      agreement: form.agreementId || 'standard',
      agreementId: form.agreementId || null,
      signed: !!form.signatureDataUrl,
      signatureDataUrl: form.signatureDataUrl || null,
      signedAt: form.signedAt || null,
      agreementSaved: form.signatureDataUrl ? (() => {
        const tmpl = (agreementTemplates || []).find(t => t.id === form.agreementId)
          || (resolveAgreementTemplate ? resolveAgreementTemplate(form.vid, 'lease') : null)
          || (agreementTemplates || []).find(t => t.appliesTo === 'lease')
          || (agreementTemplates || []).find(t => t.isDefault)
          || (agreementTemplates || [])[0];
        const veh = vehicles.find(v => v.id === form.vid);
        const customerDisplay = form.customerType === 'company' ? (form.customerCompany || form.customerName) : form.customerName;
        const ctxForSnap = buildContractContext({
          rental: {
            customer: customerDisplay,
            customerCompany: form.customerCompany,
            passport: form.passport, licence: form.licence,
            email: form.email, phone: form.phone, vatNumber: form.vatNumber,
            startLabel: new Date(form.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
            endLabel:   new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
            termMonths: form.termMonths,
            monthlyRate: form.monthlyRate,
            annualMileageCap: parseInt(form.annualMileageCap) || 0,
            deposit: form.deposit, depositMethod: form.depositMethod,
            agreedPrice: totalContract,
            includedServices: form.includedServices,
            pickupLocName: pickLoc?.name,
          },
          vehicle: veh,
          company: companySettings,
          signatureDataUrl: form.signatureDataUrl,
          signedAt: form.signedAt,
        });
        return {
          templateId: tmpl?.id || null,
          templateName: tmpl?.name || 'Long-term Lease Agreement',
          body: tmpl?.body || '',
          renderedHtml: tmpl ? renderContract(tmpl, ctxForSnap) : '',
          context: ctxForSnap,
          signedAt: form.signedAt || new Date().toISOString(),
          signedAtFormatted: form.signedAt ? new Date(form.signedAt).toLocaleString('en-GB') : new Date().toLocaleString('en-GB'),
          customerName: customerDisplay,
          vehicleName: veh?.name || '',
          vehiclePlate: veh?.plate || '',
          signData: form.signatureDataUrl,
        };
      })() : null,
      photosPre: LEASE_PHOTO_SLOTS.filter(l => photos[l]).map(l => photos[l]),
      photosRet: [],
      condPre: cond,
      condPreNote: condNote,
      createdAt: new Date().toISOString().slice(0, 10),
      // Lineage — useful so operator can see the chain of renewals over years
      ...(isRenewal && preset?.previousLeaseId ? { renewedFrom: preset.previousLeaseId } : {}),
    };

    // ── AUTO-GENERATE FIRST INVOICE ────────────────────────────────────
    // Cadence rules:
    //   advance / net_15 / net_30 → bill IMMEDIATELY on lease start
    //   end_of_month              → wait until end of first month
    let firstInvoice = null;
    if (['advance', 'net_15', 'net_30'].includes(form.billingCadence)) {
      const monthlyRate = parseFloat(form.monthlyRate) || 0;
      // Use lease-level VAT settings (override company defaults)
      const vatRate = parseFloat(form.vatRate) || parseFloat(companySettings?.vatRate || 19);
      const vatMode = form.vatMode || companySettings?.vatMode || 'inclusive';
      const subtotal = vatMode === 'inclusive' ? monthlyRate / (1 + vatRate / 100) : monthlyRate;
      const vatAmt   = vatMode === 'inclusive' ? monthlyRate - subtotal : monthlyRate * vatRate / 100;
      const total    = vatMode === 'inclusive' ? monthlyRate : monthlyRate + vatAmt;
      const billingDate = new Date(form.start);
      const monthStart = new Date(billingDate.getFullYear(), billingDate.getMonth(), 1);
      const monthEnd   = new Date(billingDate.getFullYear(), billingDate.getMonth() + 1, 0);
      const billingKey = billingDate.toISOString().slice(0, 7);
      const fmtShort   = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const periodStr  = `${fmtShort(monthStart)} – ${fmtShort(monthEnd)}`;
      const vehicle    = vehicles.find(v => v.id === form.vid);
      const vehicleName = vehicle?.name || '';

      const dueDays = form.billingCadence === 'net_15' ? 15
                   : form.billingCadence === 'net_30' ? 30
                   : 0;
      const dueDateObj = new Date(billingDate.getFullYear(), billingDate.getMonth(), billingDate.getDate() + dueDays);

      const methodLabelMap = {
        bank: 'Bank Transfer',
        sepa: 'SEPA Direct Debit',
        stripe: 'Card (Stripe)',
        paymentlink: 'Payment Link',
        cash: 'Cash',
      };

      const invNum = nextInvoiceNum();
      firstInvoice = {
        id: 'inv-' + Date.now().toString(36),
        num: invNum, status: 'pending',
        issued: fmtShort(billingDate),
        issuedAt: new Date().toISOString(),
        customer: customerDisplay,
        customerCompany: form.customerCompany || null,
        customerVatNumber: form.vatNumber || null,
        customerId: custId,
        vehicle: vehicleName,
        vid: form.vid,
        contract: id,
        method: form.payMethod || 'bank',
        methodLabel: methodLabelMap[form.payMethod] || 'Bank Transfer',
        issueDate: billingDate.toISOString().slice(0, 10),
        dueDate:   dueDateObj.toISOString().slice(0, 10),
        due:       fmtShort(dueDateObj),
        lines: [{
          desc: `${vehicleName || 'Lease'} · ${periodStr}`,
          qty: 1, unit: subtotal, total: subtotal,
        }],
        subtotal, vatRate, vatAmt, vatMode,
        discount: 0, discType: 'eur', total,
        notes: `Lease ${id} · Month 1 of ${form.termMonths} · ${billingDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`,
        isLeaseInvoice: true,
        leaseMonthKey: billingKey,
        email: form.email || '',
      };
      lease.invoicedMonths = [billingKey];
    }

    addRental(lease);
    if (firstInvoice) {
      addInvoice(firstInvoice);
      showToast(`Lease ${id} created · pro forma issued for first month`);
    } else {
      showToast(`Lease ${id} created · first pro forma will be issued ${form.billingCadence === 'end_of_month' ? 'end of month' : 'on schedule'}`);
    }
    closeModal('add-lease');
  }

  return (
    <Modal open={!!open} size="lg" onClose={() => closeModal('add-lease')}>
      <ModalHeader
        title={isRenewal ? '🔄 Renew Lease' : 'Add Lease'}
        subtitle={isRenewal
          ? `Renewing contract — terms carried over, edit anything below before creating`
          : 'Long-term contract — monthly recurring billing'}
        onClose={() => closeModal('add-lease')}
      />
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Step progress header */}
          <div style={{ display: 'flex', gap: 4, padding: '0 0 12px', borderBottom: '1px solid var(--bd)' }}>
            {LEASE_STEPS.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              return (
                <div key={n} style={{
                  flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 600,
                  padding: '8px 6px', borderRadius: 'var(--r)',
                  background: active ? 'var(--ac-l)' : done ? 'var(--gr-l)' : 'transparent',
                  color: active ? 'var(--ac)' : done ? 'var(--gr)' : 'var(--tx3)',
                  border: `1px solid ${active ? 'var(--ac-b)' : done ? 'var(--gr-b)' : 'transparent'}`,
                }}>
                  {done ? '✓ ' : `${n}. `}{label}
                </div>
              );
            })}
          </div>

          {isRenewal && (
            <div style={{
              background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)',
              padding: '10px 12px', fontSize: 11, color: 'var(--bl)',
            }}>
              🔄 <strong>Renewal mode</strong> — terms carried over from the previous lease.
              Review and edit anything below if needed, then click <strong>Create Lease</strong> on the final step.
              {preset?.previousLeaseId && (
                <span style={{ display: 'block', marginTop: 4, fontSize: 10, color: 'var(--tx3)' }}>
                  Previous lease: <code style={{ fontFamily: "'DM Mono',monospace" }}>{preset.previousLeaseId}</code>
                </span>
              )}
            </div>
          )}

          {/* ═══ STEP 1: Vehicle & Customer ═══════════════════════════ */}
          {step === 1 && <>

          {/* ── CUSTOMER ─────────────────────────────────────────────── */}
          <div>
            <div className="sdiv" style={{ marginBottom: 8 }}>Customer</div>

            {/* B2B / B2C tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[
                { val: 'company',    label: '🏢 Business (B2B)' },
                { val: 'individual', label: '👤 Individual (B2C)' },
              ].map(opt => (
                <div key={opt.val}
                  onClick={() => setForm(p => ({ ...p, customerType: opt.val }))}
                  style={{
                    flex: 1, padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600,
                    border: `1.5px solid ${form.customerType === opt.val ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 'var(--r)', cursor: 'pointer',
                    background: form.customerType === opt.val ? 'var(--ac-l)' : 'var(--sur)',
                    color: form.customerType === opt.val ? 'var(--ac)' : 'var(--tx2)',
                  }}>
                  {opt.label}
                </div>
              ))}
            </div>

            <div className="fg" style={{ margin: 0, marginBottom: 8 }}>
              <label className="fl">Select Customer</label>
              <Combobox
                value={form.customerId}
                items={customers.map(c => ({
                  id: c.id,
                  icon: c.company ? '🏢' : '👤',
                  label: c.company || `${c.fname} ${c.lname}`.trim(),
                  sublabel: [c.phone, c.email].filter(Boolean).join(' · ') || undefined,
                }))}
                placeholder="🔍 Search by name, company, phone or email…"
                onSelect={(id) => pickCustomer(id)}
                onClear={() => pickCustomer('')}
                extraOption={{ id: '__new__', label: '+ New customer…' }}
              />
            </div>

            {(isNewCust || form.customerId) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {form.customerType === 'company' && (
                  <>
                    <div className="fg" style={{ margin: 0 }}>
                      <label className="fl">Company name *</label>
                      <input type="text" className="fi" placeholder="e.g. ACME Corp Ltd." {...F('customerCompany')} />
                    </div>
                    <div className="fg" style={{ margin: 0 }}>
                      <label className="fl">VAT number</label>
                      <input type="text" className="fi" placeholder="e.g. CY10234567B" {...F('vatNumber')} />
                    </div>
                    <div className="fg" style={{ margin: 0 }}>
                      <label className="fl">Contact person</label>
                      <input type="text" className="fi" placeholder="Person we deal with" {...F('customerName')} />
                    </div>
                  </>
                )}
                {form.customerType === 'individual' && (
                  <div className="fg" style={{ gridColumn: '1/-1', margin: 0 }}>
                    <label className="fl">Full name *</label>
                    <input type="text" className="fi" placeholder="First and last name" {...F('customerName')} />
                  </div>
                )}
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Phone</label>
                  <input type="tel" className="fi" placeholder="+357 99 000 000" {...F('phone')} />
                </div>
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Email</label>
                  <input type="email" className="fi" placeholder="billing@company.com" {...F('email')} />
                </div>
              </div>
            )}
          </div>

          {/* ── VEHICLE ─────────────────────────────────────────────── */}
          <div>
            <div className="sdiv" style={{ marginBottom: 8 }}>Vehicle</div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Select Vehicle *</label>
              <Combobox
                value={form.vid}
                items={availableVehicles.map(v => ({
                  id: v.id,
                  icon: v.icon || '🚗',
                  label: v.name || v.plate,
                  sublabel: [
                    v.type,
                    v.plate,
                    v.priceDay ? `€${v.priceDay}/day ref.` : '',
                  ].filter(Boolean).join(' · '),
                }))}
                placeholder="🔍 Search by name, plate or type…"
                onSelect={(id) => setForm(p => ({ ...p, vid: id }))}
                onClear={() => setForm(p => ({ ...p, vid: '' }))}
              />
              {selectedVehicle && (
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                  Once this lease is active, the vehicle is blocked from short-term bookings for {form.termMonths} months.
                </div>
              )}
            </div>
          </div>

          </>}{/* ═══ end Step 1 ═══════════════════════════════════════ */}

          {/* ═══ STEP 2: Lease Terms ═════════════════════════════════ */}
          {step === 2 && <>

          {/* ── TERM + PRICING ─────────────────────────────────────── */}
          <div>
            <div className="sdiv" style={{ marginBottom: 8 }}>Contract Terms</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Start date *</label>
                <input type="date" className="fi" {...F('start')} />
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Term *</label>
                <select className="fi" value={form.termMonths} onChange={e => setForm(p => ({ ...p, termMonths: parseInt(e.target.value) }))}>
                  {TERM_OPTIONS.map(t => <option key={t.months} value={t.months}>{t.label}</option>)}
                </select>
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">End date</label>
                <input type="date" className="fi" value={endDate} disabled style={{ background: 'var(--sur2)', cursor: 'not-allowed' }} />
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Pickup location</label>
                <select className="fi" {...F('pickupLocId')}>
                  {(locations || []).map(l => <option key={l.id} value={l.id}>{l.icon || '📍'} {l.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Monthly rate (€) *</label>
                <input type="number" className="fi" placeholder="e.g. 850" min="0" step="0.01" {...F('monthlyRate')} />
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Deposit (€)</label>
                <input type="number" className="fi" placeholder="0" min="0" {...F('deposit')} />
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Annual mileage cap</label>
                <select className="fi" value={form.annualMileageCap} onChange={e => setForm(p => ({ ...p, annualMileageCap: parseInt(e.target.value) }))}>
                  {MILEAGE_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>

            {/* ── VAT (per-lease, overrides company default) ─────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginTop: 8 }}>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">VAT treatment</label>
                <div style={{ display: 'flex', gap: 0, border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, vatMode: 'inclusive' }))}
                    style={{
                      flex: 1, padding: '8px 10px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: form.vatMode === 'inclusive' ? 'var(--ac)' : 'var(--sur)',
                      color: form.vatMode === 'inclusive' ? '#fff' : 'var(--tx2)',
                    }}
                  >
                    🟢 Monthly rate INCLUDES VAT
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, vatMode: 'exclusive' }))}
                    style={{
                      flex: 1, padding: '8px 10px', fontSize: 11, fontWeight: 600, border: 'none', borderLeft: '1px solid var(--bd)', cursor: 'pointer',
                      background: form.vatMode === 'exclusive' ? 'var(--ac)' : 'var(--sur)',
                      color: form.vatMode === 'exclusive' ? '#fff' : 'var(--tx2)',
                    }}
                  >
                    ➕ VAT added ON TOP
                  </button>
                </div>
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                  Overrides company default. Most B2C: inclusive · Most B2B: + VAT (NET-30 invoicing).
                </div>
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">VAT rate (%)</label>
                <input
                  type="number" className="fi"
                  placeholder={`Default ${companySettings?.vatRate || 19}%`}
                  min="0" max="100" step="0.5"
                  {...F('vatRate')}
                />
              </div>
            </div>

            {/* Live preview of what customer will see */}
            {form.monthlyRate && (() => {
              const rate = parseFloat(form.monthlyRate) || 0;
              const vat = parseFloat(form.vatRate) || parseFloat(companySettings?.vatRate || 19);
              if (form.vatMode === 'inclusive') {
                const net = rate / (1 + vat / 100);
                const vatAmt = rate - net;
                return (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 6, fontSize: 11, color: 'var(--gr)' }}>
                    🟢 Customer is billed <strong>€{rate.toFixed(2)}</strong> total per month
                    (Net €{net.toFixed(2)} + €{vatAmt.toFixed(2)} VAT @ {vat}%)
                  </div>
                );
              }
              const vatAmt = rate * vat / 100;
              const total = rate + vatAmt;
              return (
                <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 6, fontSize: 11, color: 'var(--am)' }}>
                  ➕ Customer is billed <strong>€{total.toFixed(2)}</strong> total per month
                  (Net €{rate.toFixed(2)} + €{vatAmt.toFixed(2)} VAT @ {vat}%)
                </div>
              );
            })()}

            {/* Contract value summary */}
            {form.monthlyRate && (
              <div style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r)',
                background: 'var(--bl-l)', border: '1px solid var(--bl-b)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              }}>
                <div style={{ fontSize: 11, color: 'var(--bl)' }}>
                  📊 <strong>Total contract value:</strong> €{totalContract.toLocaleString()} ({form.termMonths} months × €{parseFloat(form.monthlyRate).toFixed(2)})
                </div>
                <div style={{ fontSize: 10, color: 'var(--bl)', fontFamily: "'DM Mono',monospace" }}>
                  {form.start || '—'} → {endDate || '—'}
                </div>
              </div>
            )}
          </div>

          {/* ── PAYMENT + EXTRAS ───────────────────────────────────── */}
          <div>
            <div className="sdiv" style={{ marginBottom: 8 }}>Payment & Billing</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Payment method</label>
                <select className="fi" {...F('payMethod')}>
                  <option value="bank">🏦 Bank Transfer</option>
                  <option value="sepa">💳 SEPA Direct Debit</option>
                  <option value="stripe">💳 Card (Stripe)</option>
                  <option value="paymentlink">🔗 Payment Link (email)</option>
                  <option value="cash">💵 Cash</option>
                </select>
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Deposit method</label>
                <input type="text" className="fi" placeholder="e.g. Bank guarantee" {...F('depositMethod')} />
              </div>
            </div>

            <div className="fg" style={{ margin: 0, marginTop: 8 }}>
              <label className="fl">Billing cadence</label>
              <select className="fi" {...F('billingCadence')}>
                {BILLING_CADENCES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>

            <div className="fg" style={{ margin: 0, marginTop: 8 }}>
              <label className="fl">Included services</label>
              <input type="text" className="fi" placeholder="e.g. Maintenance, Insurance, Roadside Assistance" {...F('includedServices')} />
            </div>

            <div className="fg" style={{ margin: 0, marginTop: 8 }}>
              <label className="fl">Internal notes</label>
              <textarea className="fi" rows={2} placeholder="Negotiated terms, special arrangements, contact preferences..." {...F('notes')} />
            </div>
          </div>

          </>}{/* ═══ end Step 2 ═══════════════════════════════════════ */}

          {/* ═══ STEP 3: Photos & Vehicle Condition (Annex A) ═══════ */}
          {step === 3 && <>
            {/* Pre-existing damage — pulled from the damages register, only unrepaired entries on this vehicle */}
            {(() => {
              if (!form.vid || !getDamagesForVehicle) return null;
              const existing = getDamagesForVehicle(form.vid).filter(d => !d.repaired);
              if (existing.length === 0) return null;
              return (
                <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--am)', marginBottom: 6 }}>
                    ⚠ Existing damage on this vehicle ({existing.length}) — already documented, not repaired yet
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 10 }}>
                    These were reported on previous rentals. Make sure your photos and notes below reflect them so the Lessee isn't charged for damage that was already there.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {existing.map(d => {
                      const dt  = DAMAGE_TYPES.find(t => t.id === d.type);
                      const sev = DAMAGE_SEVERITIES.find(s => s.id === d.severity);
                      return (
                        <div key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '8px 10px' }}>
                          <span style={{ fontSize: 18 }}>{dt?.icon || '⚠️'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>
                              {dt?.label || d.type} · {d.location}
                              {sev && <span style={{ fontSize: 9, marginLeft: 8, padding: '1px 6px', borderRadius: 8, background: sev.color, color: '#fff', fontWeight: 700, textTransform: 'uppercase' }}>{sev.label}</span>}
                            </div>
                            {d.description && <div style={{ fontSize: 10, color: 'var(--tx2)', marginTop: 2 }}>{d.description}</div>}
                            <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>
                              Reported {d.reportedDate}{d.rentalId ? ` · contract ${d.rentalId}` : ''}{d.customerName ? ` · ${d.customerName}` : ''}
                            </div>
                            {Array.isArray(d.photos) && d.photos.length > 0 && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                                {d.photos.slice(0, 4).map((src, i) => (
                                  <img key={i} src={src} alt="damage" onClick={() => window.open(src, '_blank')}
                                    style={{ width: 50, height: 38, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--bd)', cursor: 'pointer' }} />
                                ))}
                                {d.photos.length > 4 && <span style={{ fontSize: 10, color: 'var(--tx3)', alignSelf: 'center' }}>+{d.photos.length - 4}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div>
              <div className="sdiv" style={{ marginBottom: 8 }}>Vehicle Condition at Delivery</div>
              <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 10 }}>
                Captured as <strong>Annex A — Vehicle Condition Report</strong> on the contract. This is what excess-damage charges at end-of-lease will be calculated against.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {LEASE_COND_OPTIONS.map(opt => (
                  <div key={opt.value} onClick={() => setCond(opt.value)} style={{
                    padding: '9px 12px',
                    border: `1.5px solid ${cond === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                    background: cond === opt.value ? 'var(--ac-l)' : 'var(--sur)',
                  }}>
                    {opt.label}
                  </div>
                ))}
              </div>
              <div className="fg" style={{ marginTop: 10 }}>
                <label className="fl">Condition notes (visible scratches, dents, interior wear, missing items…)</label>
                <textarea className="fi" rows={2} value={condNote} onChange={e => setCondNote(e.target.value)} placeholder="Describe any existing damage or notes for the condition report..." />
              </div>
            </div>

            {/* Handover photos — 6 named slots, upload or live camera */}
            <div>
              <div className="sdiv" style={{ marginBottom: 8 }}>
                Handover Photos
                <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx3)', marginLeft: 8 }}>
                  {Object.values(photos).filter(Boolean).length} / {LEASE_PHOTO_SLOTS.length} added
                </span>
              </div>
              {/* Hidden file input shared by all slots */}
              <input ref={slotFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onSlotFile} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {LEASE_PHOTO_SLOTS.map(label => (
                  <div key={label} style={{
                    border: `2px ${photos[label] ? 'solid var(--gr-b)' : 'dashed var(--bd)'}`,
                    borderRadius: 'var(--r)', overflow: 'hidden',
                    background: photos[label] ? 'transparent' : 'var(--sur2)',
                  }}>
                    {photos[label] ? (
                      <>
                        <img src={photos[label]} alt={label}
                          style={{ width: '100%', height: 96, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => window.open(photos[label], '_blank')} />
                        <div style={{ padding: '4px 4px 5px', display: 'flex', gap: 3, justifyContent: 'center' }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotFile(label)} title="Re-upload">📁</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotCamera(label)} title="Retake">📷</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9, color: 'var(--ac)' }} onClick={() => removeSlotPhoto(label)} title="Remove">✕</button>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: 9, color: 'var(--gr)', paddingBottom: 4, fontWeight: 600 }}>✓ {label}</div>
                      </>
                    ) : (
                      <div style={{ padding: '18px 8px', textAlign: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)' }}
                           onClick={() => openSlotFile(label)}>
                        <div style={{ fontSize: 22, marginBottom: 4 }}>📷</div>
                        <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 9 }}>+ Upload</div>
                        <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: 9, padding: '2px 8px' }}
                                onClick={e => { e.stopPropagation(); openSlotCamera(label); }}>
                          📸 Camera
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>}{/* ═══ end Step 3 ═══════════════════════════════════════ */}

          {/* ═══ STEP 4: Agreement & Sign ═════════════════════════════ */}
          {step === 4 && <>

          {/* ── AGREEMENT + SIGNATURE ──────────────────────────────── */}
          <div>
            <div className="sdiv" style={{ marginBottom: 8 }}>📋 Agreement & Signature</div>

            <div className="fg" style={{ margin: 0, marginBottom: 8 }}>
              <label className="fl">Contract template</label>
              <select className="fi" {...F('agreementId')}>
                <option value="">— Default (Standard agreement) —</option>
                {(agreementTemplates || []).map(t => (
                  <option key={t.id} value={t.id}>{t.icon || '📄'} {t.name}</option>
                ))}
              </select>
              <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                Manage templates in <strong>Agreements</strong> page.
              </div>
            </div>

            {/* Preview / Print contract row — always available */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowPreview(true)}
              >
                📋 Preview contract
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const { tmpl, ctx } = getCurrentTmplCtx();
                  printContract(tmpl, ctx);
                }}
              >
                🖨️ Print {form.signatureDataUrl ? '(with signature)' : '(blank — for client to sign)'}
              </button>
            </div>

            {/* Signature pad */}
            <div style={{
              background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)',
              padding: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>Customer signature</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                    {form.signatureDataUrl
                      ? <>✅ Signed{form.signedAt ? ` on ${new Date(form.signedAt).toLocaleString('en-GB')}` : ''}</>
                      : 'Not signed yet — optional, can sign later'}
                  </div>
                </div>
                {!form.signatureDataUrl && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setShowSignature(true);
                      // Reset canvas when opening
                      setTimeout(() => {
                        const c = canvasRef.current;
                        if (c) {
                          const ctx = c.getContext('2d');
                          ctx.fillStyle = '#fff';
                          ctx.fillRect(0, 0, c.width, c.height);
                          ctx.strokeStyle = '#000';
                          ctx.lineWidth = 2;
                          ctx.lineCap = 'round';
                        }
                      }, 50);
                    }}
                  >
                    ✍️ Capture signature
                  </button>
                )}
                {form.signatureDataUrl && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setForm(p => ({ ...p, signatureDataUrl: '', signedAt: '' }))}
                  >
                    Clear
                  </button>
                )}
              </div>

              {form.signatureDataUrl && !showSignature && (
                <>
                  <img
                    src={form.signatureDataUrl}
                    alt="signature"
                    style={{ maxWidth: 280, height: 'auto', background: '#fff', border: '1px solid var(--bd)', borderRadius: 4, display: 'block' }}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      const { tmpl, ctx } = getCurrentTmplCtx();
                      printContract(tmpl, ctx);
                    }}
                  >
                    🖨️ Print contract (with signature)
                  </button>
                </>
              )}

              {showSignature && (
                <div>
                  <div style={{ background: '#fff', border: '2px dashed var(--bd2)', borderRadius: 6, display: 'inline-block' }}>
                    <canvas
                      ref={canvasRef}
                      width={420}
                      height={140}
                      style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                      onMouseDown={(e) => {
                        drawingRef.current = true;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const ctx = e.currentTarget.getContext('2d');
                        ctx.beginPath();
                        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
                      }}
                      onMouseMove={(e) => {
                        if (!drawingRef.current) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const ctx = e.currentTarget.getContext('2d');
                        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
                        ctx.stroke();
                      }}
                      onMouseUp={() => { drawingRef.current = false; }}
                      onMouseLeave={() => { drawingRef.current = false; }}
                      onTouchStart={(e) => {
                        drawingRef.current = true;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const t = e.touches[0];
                        const ctx = e.currentTarget.getContext('2d');
                        ctx.beginPath();
                        ctx.moveTo(t.clientX - rect.left, t.clientY - rect.top);
                      }}
                      onTouchMove={(e) => {
                        if (!drawingRef.current) return;
                        e.preventDefault();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const t = e.touches[0];
                        const ctx = e.currentTarget.getContext('2d');
                        ctx.lineTo(t.clientX - rect.left, t.clientY - rect.top);
                        ctx.stroke();
                      }}
                      onTouchEnd={() => { drawingRef.current = false; }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        const c = canvasRef.current;
                        if (!c) return;
                        const ctx = c.getContext('2d');
                        ctx.fillStyle = '#fff';
                        ctx.fillRect(0, 0, c.width, c.height);
                      }}
                    >
                      Erase
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowSignature(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        const c = canvasRef.current;
                        if (!c) return;
                        const dataUrl = c.toDataURL('image/png');
                        setForm(p => ({
                          ...p,
                          signatureDataUrl: dataUrl,
                          signedAt: new Date().toISOString(),
                        }));
                        setShowSignature(false);
                      }}
                    >
                      ✓ Save signature
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          </>}{/* ═══ end Step 4 ═══════════════════════════════════════ */}
        </div>

        {/* Live camera overlay — opened from any photo slot */}
        {camera && (
          <CameraCapture
            label={camera.label}
            onCapture={dataUrl => { camera.setPhoto(dataUrl); setCamera(null); }}
            onClose={() => setCamera(null)}
          />
        )}

        {/* Contract preview overlay — portal-rendered to escape modal clipping */}
        {showPreview && createPortal(
          <div onClick={() => setShowPreview(false)}
               style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
                 style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  📋 Contract preview {form.signatureDataUrl ? '· Signed' : '· Not signed yet'}
                </div>
                <button onClick={() => setShowPreview(false)}
                        style={{ fontSize: 22, cursor: 'pointer', border: 'none', background: 'none', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, background: 'var(--sur2)' }}>
                {(() => {
                  const { tmpl, ctx } = getCurrentTmplCtx();
                  return tmpl
                    ? <ContractDocument template={tmpl} context={ctx} paperLook={true} />
                    : <div style={{ padding: 30, color: 'var(--tx3)', textAlign: 'center' }}>No agreement template available. Add one in <strong>Agreements</strong>.</div>;
                })()}
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowPreview(false)}>Close</button>
                <button className="btn btn-primary" onClick={() => {
                  const { tmpl, ctx } = getCurrentTmplCtx();
                  printContract(tmpl, ctx);
                }}>🖨️ Print / PDF</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </ModalBody>
      <ModalFooter>
        <div style={{ fontSize: 10, color: 'var(--tx3)', marginRight: 'auto' }}>
          Step {step} of {LEASE_STEPS.length} — {LEASE_STEPS[step - 1]}
        </div>
        <button className="btn btn-ghost" onClick={() => closeModal('add-lease')}>Cancel</button>
        {step > 1 && (
          <button className="btn btn-ghost" onClick={() => setStep(s => Math.max(1, s - 1))}>← Back</button>
        )}
        {step < LEASE_STEPS.length ? (
          <button className="btn btn-primary" onClick={() => {
            // Light per-step validation
            if (step === 1) {
              if (!form.customerName && !form.customerCompany) { showToast('Pick or add a customer first'); return; }
              if (!form.vid) { showToast('Pick a vehicle first'); return; }
            }
            if (step === 2) {
              if (!form.start || !form.termMonths || !form.monthlyRate) { showToast('Start date, term and monthly rate are required'); return; }
            }
            setStep(s => Math.min(LEASE_STEPS.length, s + 1));
          }}>Next →</button>
        ) : (
          <button className="btn btn-primary" onClick={submit}>
            {isRenewal ? '🔄 Create Renewal' : '📅 Create Lease'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
